import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationAPI, InAppNotification } from '../services/api';
import './NotificationDropdown.css';
import { useTranslation } from 'react-i18next';

interface NotificationDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onUnreadCountChange?: (count: number) => void;
}

const NotificationDropdown: React.FC<NotificationDropdownProps> = ({ isOpen, onClose, onUnreadCountChange }) => {
  const [notifications, setNotifications] = useState<InAppNotification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const { t, i18n } = useTranslation(['notifications', 'common']);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await notificationAPI.list(false, 10, 0);
      const fetched = response.data.notifications;
      setNotifications(fetched);
      const unread = fetched.filter(n => !n.is_read).length;
      if (typeof onUnreadCountChange === 'function') {
        onUnreadCountChange(unread);
      }
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [onUnreadCountChange]);

  useEffect(() => {
    if (isOpen) {
      fetchNotifications();
    }
  }, [isOpen, fetchNotifications]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        handleClose();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, onClose, notifications]);

  const markDisplayedAsRead = async () => {
    try {
      // Mark all as read server-side to avoid badge inconsistencies across pages
      await notificationAPI.markAllAsRead();

      // Update local state to reflect the change
      setNotifications(prev => {
        const updated = prev.map(n => ({
          ...n,
          is_read: true,
          read_at: new Date().toISOString()
        }));
        if (typeof onUnreadCountChange === 'function') {
          const unreadAfter = updated.filter(n => !n.is_read).length;
          onUnreadCountChange(unreadAfter);
        }
        return updated;
      });
    } catch (error) {
      console.error('Failed to mark notifications as read:', error);
    }
  };

  const handleClose = async () => {
    // Mark all displayed notifications as read before closing
    await markDisplayedAsRead();
    onClose();
  };

  const handleNotificationClick = async (notification: InAppNotification) => {
    // Mark as read if unread
    if (!notification.is_read) {
      try {
        await notificationAPI.markAsRead(notification.id);
        // Update local state
        setNotifications(prev => {
          const updated = prev.map(n =>
            n.id === notification.id
              ? { ...n, is_read: true, read_at: new Date().toISOString() }
              : n
          );
          if (typeof onUnreadCountChange === 'function') {
            const unreadAfter = updated.filter(n => !n.is_read).length;
            onUnreadCountChange(unreadAfter);
          }
          return updated;
        });
      } catch (error) {
        console.error('Failed to mark notification as read:', error);
      }
    }

    onClose();

    // Navigate to deep link if available
    if (notification.deep_link) {
      navigate(notification.deep_link);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationAPI.markAllAsRead();
      // Update local state
      setNotifications(prev => {
        const updated = prev.map(n => ({ ...n, is_read: true, read_at: new Date().toISOString() }));
        if (typeof onUnreadCountChange === 'function') {
          onUnreadCountChange(0);
        }
        return updated;
      });
    } catch (error) {
      console.error('Failed to mark all as read:', error);
    }
  };

  const handleViewAll = async () => {
    await handleClose();
    navigate('/notifications');
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffSecs = Math.floor(diffMs / 1000);
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);

    const locale = i18n.language.startsWith('en') ? 'en-US' : 'pt-BR';

    // Less than 60 seconds
    if (diffSecs < 60) return t('common:time.justNow');

    // Less than 60 minutes
    if (diffMins < 60) {
      return t('common:time.minutesAgo', { count: diffMins });
    }

    // Less than 24 hours
    if (diffHours < 24) {
      return t('common:time.hoursAgo', { count: diffHours });
    }

    // More than 1 day - show date with time
    return date.toLocaleString(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDocumentName = (notification: InAppNotification): string | null => {
    // Try to extract document name from deep_link
    if (notification.deep_link && notification.deep_link.includes('/documents/')) {
      // This is a simple extraction; backend should ideally provide document_name
      return t('notifications:dropdown.document');
    }
    return null;
  };

  if (!isOpen) return null;

  const unreadCount = notifications.filter(n => !n.is_read).length;

  return (
    <div className="notification-dropdown" ref={dropdownRef}>
      <div className="notification-dropdown-header">
        <h3>{t('notifications:dropdown.title')}</h3>
        {unreadCount > 0 && (
          <button className="mark-all-read-btn" onClick={handleMarkAllAsRead}>
            {t('notifications:page.markAllRead')}
          </button>
        )}
      </div>

      <div className="notification-dropdown-content">
        {loading ? (
          <div className="notification-dropdown-loading">
            <div className="spinner-small"></div>
            <p>{t('common:buttons.loading')}</p>
          </div>
        ) : notifications.length === 0 ? (
          <div className="notification-dropdown-empty">
            <div className="empty-bell-icon">🔔</div>
            <p>{t('notifications:dropdown.empty')}</p>
          </div>
        ) : (
          <div className="notification-dropdown-list">
            {notifications.map(notification => (
              <div
                key={notification.id}
                className={`notification-dropdown-item ${!notification.is_read ? 'unread' : ''}`}
                onClick={() => handleNotificationClick(notification)}
              >
                {!notification.is_read && <span className="notification-dropdown-dot"></span>}
                <div className="notification-dropdown-item-content">
                  <h4>{notification.title}</h4>
                  <div className="notification-dropdown-item-meta">
                    {getDocumentName(notification) && (
                      <span className="notification-document-name">{getDocumentName(notification)}</span>
                    )}
                    <span className="notification-time">{formatDate(notification.created_at)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {notifications.length > 0 && (
        <div className="notification-dropdown-footer">
          <button className="view-all-btn" onClick={handleViewAll}>
            {t('notifications:dropdown.viewAll')}
          </button>
        </div>
      )}
    </div>
  );
};

export default NotificationDropdown;
